import { useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import { setSelected } from "../../../redux/slice/selectionSlice";
import { useGetPlansQuery, useDeletePlanMutation } from "../../../redux/api";
import type { Plan } from "../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEdit,
  IconTrash,
} from "../../../components/icons";
import { RowMenu } from "../../../components/RowMenu";

const ENTITY = "plan";
const DETAIL_PATH = "/plan";
const COLUMN_COUNT = 6;

// Platform Owner → Plans (list page). Subscription plan catalogue.
const Plans = () => {
  const dispatch = useAppDispatch();

  const {
    data: plans = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetPlansQuery();

  const [deletePlan] = useDeletePlanMutation();

  const [search, setSearch] = useState("");


  const openDetail = (id: string | null) => {
    dispatch(setSelected({ entity: ENTITY, id }));
    dispatch(navigate(DETAIL_PATH));
  };

  const handleDelete = async (plan: Plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try {
      await deletePlan(plan.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete plan.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter((p: Plan) =>
      [p.code, p.name, p.currency].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [plans, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load plans"
    : null;

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Plans</h2>
            <p className="panel__sub">
              Showing {visible.length} of {plans.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search plans…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New plan
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Monthly</th>
                <th>Yearly</th>
                <th>Currency</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading plans…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {plans.length === 0
                      ? "No plans yet."
                      : "No plans match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((plan: Plan) => (
                  <tr key={plan.id}>
                    <td>
                      <span className="pill pill--neutral">{plan.code}</span>
                    </td>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => openDetail(plan.id)}
                      >
                        {plan.name}
                      </button>
                    </td>
                    <td className="cell-date">
                      {plan.monthlyPrice?.toLocaleString?.() ?? plan.monthlyPrice}
                    </td>
                    <td className="cell-date">
                      {plan.yearlyPrice?.toLocaleString?.() ?? plan.yearlyPrice}
                    </td>
                    <td>{plan.currency}</td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "Edit plan",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(plan.id),
                          },
                          {
                            label: "Delete plan",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(plan),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
};

export default Plans;
